<template>
  <NuxtPicture
    :alt="alt"
    :sizes="sizes"
    :height="height"
    :width="width"
    provider="snipes"
    densities="x1"
    :src="computedImageWithPath"
    :loading="imageLoading"
    :preload="preload"
    :fetchpriority="preload ? 'high' : 'auto'"
    :class="{
      'picture-contain': fit === 'contain',
      'picture-cover': fit === 'cover',
      'm-auto h-[90%]': isCentered,
      'mix-blend-darken': withMixBlendDarken,
    }"
    data-testid="product-image"
    class="picture block"
    @load="load"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ProductImage } from '@scayle/storefront-nuxt'
import { NuxtPicture } from '#components'

const {
  sizes = '',
  isCentered = false,
  withMixBlendDarken = true,
  fit = 'contain',
  imageLoading = 'eager',
  preload = false,
  load = () => {},
  image,
  productPath = '',
} = defineProps<{
  image: ProductImage | string
  sizes?: string
  fit?: 'contain' | 'cover'
  alt: string
  imageLoading?: 'lazy' | 'eager'
  preload?: boolean
  load?: () => void
  isCentered?: boolean
  withMixBlendDarken?: boolean
  height?: number | string
  width?: number | string
  productPath?: string
}>()

const computedImage = computed(() =>
  typeof image === 'string' ? image : image.hash,
)

// Encode product path in the src for the provider to use
const computedImageWithPath = computed(() => {
  const imageHash = computedImage.value
  if (!imageHash || !productPath) {
    return imageHash
  }

  // Extract the last number from the imageHash using a simple approach
  const numbers = imageHash.match(/\d+/g)
  const lastNumber = numbers ? numbers[numbers.length - 1] : ''

  // Append product path as a query parameter so the provider can extract it
  const combinedPath = `${productPath}-${lastNumber}`
  return `${imageHash}?productPath=${encodeURIComponent(combinedPath)}`
})
</script>

<!-- Styles for .picture classes are in assets/css/main.css -->
